import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateWebhookSubscription,
  AdminFetchWebhookDeliveries,
  AdminFetchWebhookSubscriptions,
  AdminSendWebhook,
  AdminUpdateWebhookSubscription,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateWebhookSubscriptionPayload,
  CreateWebhookSubscriptionResult,
  FetchWebhookDeliveriesPayload,
  SendWebhookPayload,
  SendWebhookResult,
  UpdateWebhookSubscriptionPayload,
  WebhookDelivery,
  WebhookSubscription,
} from 'src/interfaces/webhook'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface FetchWebhookSubscriptionsPayload {
  partnerId?: string
}

export function* onFetchWebhookSubscriptions(
  action: AsyncAction<FetchWebhookSubscriptionsPayload>,
) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchWebhookSubscriptions.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // partnerId is a PATH param on this endpoint, not a query param.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchWebhookSubscriptions,
      {
        method: 'GET',
        params: [{ field: ':partnerId', value: partnerId }],
      },
    )

    // The endpoint answers with a `{ data }` envelope; the normalizer handles both shapes.
    const subscriptions = normalizeApiListPayload<WebhookSubscription>(payload)

    // `WebhookSubscription` omits `secret` at the type level, but the DTO still puts the encrypted
    // column on the wire and TypeScript strips nothing at runtime. It is deleted here so no
    // secret material — not even ciphertext — reaches the store off this endpoint's rows.
    const safeSubscriptions = subscriptions.map((subscription) => {
      const safeSubscription: WebhookSubscription = { ...subscription }

      delete (safeSubscription as Partial<CreateWebhookSubscriptionResult>).secret

      return safeSubscription
    })

    yield put(AdminFetchWebhookSubscriptions.success(safeSubscriptions))

    if (action.next) {
      action.next(null, safeSubscriptions)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.webhook.onFetchWebhookSubscriptions] error',
    })

    yield put(AdminFetchWebhookSubscriptions.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load webhook subscriptions'))
    }
  }
}

export function* onCreateWebhookSubscription(
  action: AsyncAction<CreateWebhookSubscriptionPayload>,
) {
  try {
    const { payload }: { payload: CreateWebhookSubscriptionResult } = yield call(
      callSecureApi,
      Endpoints.AdminCreateWebhookSubscription,
      {
        method: 'POST',
        body: action.payload,
      },
    )

    // This response carries the ONLY plaintext secret that will ever exist for this
    // subscription — every later read returns the encrypted column. It must therefore never
    // become an action payload: `redux-logger` prints every action to the console, AppLogger's
    // redux middleware forwards them, and Sentry keeps them as breadcrumbs plus a full state
    // attachment. The success action carries the subscription WITHOUT the secret; the untouched
    // response goes only to `action.next`, which resolves the awaited promise in the component
    // and never enters the store.
    const subscriptionWithoutSecret: WebhookSubscription = { ...payload }

    delete (subscriptionWithoutSecret as Partial<CreateWebhookSubscriptionResult>).secret

    yield put(AdminCreateWebhookSubscription.success(subscriptionWithoutSecret))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.webhook.onCreateWebhookSubscription] error',
    })

    yield put(AdminCreateWebhookSubscription.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create webhook subscription'))
    }
  }
}

export function* onUpdateWebhookSubscription(
  action: AsyncAction<UpdateWebhookSubscriptionPayload>,
) {
  const subscriptionId = action.payload?.subscriptionId

  if (!subscriptionId) {
    yield put(AdminUpdateWebhookSubscription.failed(new Error('Missing subscriptionId')))

    if (action.next) {
      action.next('Missing subscriptionId')
    }

    return
  }

  // The body is built field by field rather than forwarded: `subscriptionId` is a path param and
  // must never appear in the body, and every body field is optional on the backend — which is
  // what lets the enable/disable toggle send `{ isActive }` alone.
  const body: Omit<UpdateWebhookSubscriptionPayload, 'subscriptionId'> = {}

  if (action.payload.callbackUrl !== undefined) {
    body.callbackUrl = action.payload.callbackUrl
  }

  if (action.payload.events !== undefined) {
    body.events = action.payload.events
  }

  if (action.payload.isActive !== undefined) {
    body.isActive = action.payload.isActive
  }

  if (action.payload.webhookVersion !== undefined) {
    body.webhookVersion = action.payload.webhookVersion
  }

  try {
    const { payload }: { payload: WebhookSubscription } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateWebhookSubscription,
      {
        method: 'PUT',
        params: [{ field: ':subscriptionId', value: subscriptionId }],
        body,
      },
    )

    // This response is a `WebhookSubscriptionPlainDto`, which still `@Expose()`s the encrypted
    // `secret` column on the wire. It must therefore never become an action payload: `redux-logger`
    // prints every action to the console and Sentry keeps them as breadcrumbs. The success action
    // carries the subscription WITHOUT the secret; the untouched response goes only to
    // `action.next`, which resolves the awaited promise in the component and never enters the store.
    const subscriptionWithoutSecret: WebhookSubscription = { ...payload }

    delete (subscriptionWithoutSecret as Partial<CreateWebhookSubscriptionResult>).secret

    yield put(AdminUpdateWebhookSubscription.success(subscriptionWithoutSecret))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.webhook.onUpdateWebhookSubscription] error',
    })

    yield put(AdminUpdateWebhookSubscription.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update webhook subscription'))
    }
  }
}

export function* onFetchWebhookDeliveries(action: AsyncAction<FetchWebhookDeliveriesPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchWebhookDeliveries.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // Flat `limit` / `offset`: AdminGetWebhookDeliveriesQueryDto declares a nested
    // PaginationQueryDto but its @Transform reads obj.limit / obj.offset off the TOP level, so a
    // nested `pagination[limit]` would be ignored and silently fall back to 20 / 0.
    // Unset filters are dropped downstream by callApi's `_.omitBy(query, _.isNil)`.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchWebhookDeliveries,
      {
        method: 'GET',
        query: {
          partnerId,
          subscriptionId: action.payload.subscriptionId,
          status: action.payload.status,
          eventType: action.payload.eventType,
          resourceId: action.payload.resourceId,
          limit: action.payload.limit,
          offset: action.payload.offset,
        },
      },
    )

    // NOT normalizeApiListPayload: that helper returns only the array, and `total` is what the
    // grid needs as `rowCount` under server pagination. The envelope is read explicitly.
    const envelope = (payload ?? {}) as { data?: WebhookDelivery[]; total?: number }
    const rawDeliveries = Array.isArray(envelope.data) ? envelope.data : []

    // Each row's left-joined `subscription` (when present) is a `WebhookSubscriptionPlainDto`,
    // which still `@Expose()`s the encrypted `secret` column on the wire even though
    // `WebhookDelivery` types `subscription` as the secret-less `WebhookSubscription`. It is
    // deleted here, mirroring the subscriptions-list strip above, so no secret material — not
    // even ciphertext — reaches the store off deliveries rows. `event` carries no secret material
    // and is left untouched.
    const deliveries = rawDeliveries.map((delivery) => {
      if (!delivery.subscription) {
        return delivery
      }

      const safeSubscription: WebhookSubscription = { ...delivery.subscription }

      delete (safeSubscription as Partial<CreateWebhookSubscriptionResult>).secret

      return { ...delivery, subscription: safeSubscription }
    })

    const total = typeof envelope.total === 'number' ? envelope.total : deliveries.length
    const page = { deliveries, total }

    yield put(AdminFetchWebhookDeliveries.success(page))

    if (action.next) {
      action.next(null, page)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.webhook.onFetchWebhookDeliveries] error',
    })

    yield put(AdminFetchWebhookDeliveries.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load webhook deliveries'))
    }
  }
}

export function* onSendWebhook(action: AsyncAction<SendWebhookPayload>) {
  const resourceId = action.payload?.resourceId

  if (!resourceId) {
    yield put(AdminSendWebhook.failed(new Error('Missing resourceId')))

    if (action.next) {
      action.next('Missing resourceId')
    }

    return
  }

  try {
    // Exactly the four fields AdminManualWebhookSendDto requires. No `eventDetails` key — the
    // backend defaults it to {}, and sending an empty object would be noise on the wire.
    const { payload }: { payload: SendWebhookResult } = yield call(
      callSecureApi,
      Endpoints.AdminSendWebhook,
      {
        method: 'POST',
        body: {
          partnerId: action.payload.partnerId,
          eventType: action.payload.eventType,
          resourceId,
          resourceType: action.payload.resourceType,
        },
      },
    )

    yield put(AdminSendWebhook.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.webhook.onSendWebhook] error' })

    yield put(AdminSendWebhook.failed(error))

    if (action.next) {
      // Both of this endpoint's preconditions fail as 400s with useful messages — the missing
      // api_integration product, and no active subscription for the chosen event. Passing the
      // backend message through unchanged is the whole point.
      action.next(resolveErrorMessage(error, 'Failed to send the test webhook'))
    }
  }
}

export function* webhookSaga() {
  // A refresh, a tab switch, or a successful mutation re-triggers these, so only the newest run
  // matters. Deliveries in particular re-fire on every filter change and page change.
  yield takeLatest(AdminFetchWebhookSubscriptions.type.REQUEST, onFetchWebhookSubscriptions)
  yield takeLatest(AdminFetchWebhookDeliveries.type.REQUEST, onFetchWebhookDeliveries)
  // Mutations must each run.
  yield takeEvery(AdminCreateWebhookSubscription.type.REQUEST, onCreateWebhookSubscription)
  yield takeEvery(AdminUpdateWebhookSubscription.type.REQUEST, onUpdateWebhookSubscription)
  yield takeEvery(AdminSendWebhook.type.REQUEST, onSendWebhook)
}
